<template>
  <v-container ref="container" v-scroll="onScroll">
    <TitleParagraphRow :value="value" />

    <v-row>
      <v-col cols="12">
        <v-row
          v-if="isMounted && !hideFilters"
          :style="{ top: `${stickyTop}px` }"
          class="sticky-lg"
          style="z-index: 1"
        >
          <v-col v-if="filtersData">
            <v-card elevation="4" rounded="0" theme="light">
              <v-card-text>
                <v-row align="center">
                  <v-col>
                    <v-card
                      class="text-h4 font-weight-light"
                      variant="flat"
                      @click="toggleFilters"
                    >
                      <v-icon
                        :icon="mdiTuneVariant"
                        color="surface-variant"
                        size="42"
                      />
                      Filters
                    </v-card></v-col
                  >

                  <v-col cols="auto">
                    <v-btn variant="text" @click="resetFilters"
                      >Reset Filters</v-btn
                    >
                    <v-btn
                      :append-icon="
                        filtersShown ? mdiChevronUp : mdiChevronDown
                      "
                      variant="text"
                      @click="toggleFilters"
                      >{{ filtersShown ? 'Hide' : 'Show' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-expand-transition>
                <v-card-text v-if="filtersShown">
                  <v-row class="pt-2">
                    <v-col
                      v-if="shownFilters.what && (whatMenu || whatProducts)"
                      cols="12"
                      md="6"
                      xl=""
                    >
                      <WhatSelect
                        ref="whatSelect"
                        :what-items="whatItems"
                        :what-menu="whatMenu"
                        :what-products="whatProducts"
                        @select="selectWhatItem"
                      />
                    </v-col>

                    <v-col
                      v-if="shownFilters.deliveryMethod"
                      cols="12"
                      md="6"
                      xl=""
                    >
                      <v-select
                        v-model="filtersData.deliveryMethod"
                        :items="deliveryMethodItems"
                        hide-details
                        label="Format"
                        variant="outlined"
                      />
                    </v-col>

                    <v-col v-if="shownFilters.who" cols="12" md="6" xl="">
                      <v-select
                        v-model="filtersData.who"
                        :items="trainerItems"
                        hide-details
                        label="Trainer"
                        variant="outlined"
                      />
                    </v-col>

                    <v-col v-if="shownFilters.when" cols="12" md="6" xl="">
                      <v-select
                        v-model="filtersData.when"
                        :items="whenItems"
                        :menu-props="{ maxHeight: 500 }"
                        hide-details
                        label="When"
                        variant="outlined"
                      />
                    </v-col>
                    <!-- TODO: Custom range -->
                  </v-row>
                </v-card-text>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="banner && banner.status === 'published'" justify="center">
          <v-col cols="12" xl="8">
            <BannerCard :value="banner" />
          </v-col>
        </v-row>

        <VariantList
          v-if="filtersData"
          ref="variantList"
          :filters-value="filtersData"
          :hide-info-button="hideInfoButton"
          :limit="!isMounted ? undefined : main ? 10 : 5"
          :show-all-button="main"
        />

        <OccasionList
          v-if="filtersData"
          ref="occasionList"
          :filters-value="filtersData"
          :hide-info-button="hideInfoButton"
          :limit="!isMounted ? undefined : main ? 10 : 5"
          :show-all-button="main"
          :show-embed-code="showEmbedCode"
        />

        <v-row
          v-if="
            !variantList?.shownItems?.length &&
            !occasionList?.shownItems?.length
          "
        >
          <v-col>
            <NoOfferingsAlert />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-if="!main" class="pt-6">
      <v-col align="center">
        <v-btn
          :append-icon="mdiArrowRight"
          :href="fullScheduleHref"
          color="secondary"
          variant="text"
        >
          View Full Schedule
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import type { components } from '#root/types/directus'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useIntervalFn, useMounted } from '@vueuse/core'
import { DateTime } from 'luxon'
import {
  MENU_BAR_HEIGHT,
  FILTER_DELIVERY_METHOD_ALL,
  FILTER_WHAT_ALL,
  FILTER_WHEN_HALF_DAY,
  FILTER_WHEN_UPCOMING,
  FILTER_WHEN_WEEKEND,
  FILTER_WHO_ALL
} from '#root/lib/consts'
import { scrollToRef } from '#root/lib/utils'
import { useDeliveryMethodsStoreOrThrow } from '#root/lib/useDeliveryMethodsStore'
import { useOccasionsStoreOrThrow } from '#root/lib/useOccasionsStore'
import { useTrainerPersonsStoreOrThrow } from '#root/lib/useTrainerPersonsStore'
import debounce from 'lodash.debounce'
import {
  mdiArrowRight,
  mdiChevronDown,
  mdiChevronUp,
  mdiTuneVariant
} from '@mdi/js'

type FiltersValue = {
  deliveryMethod?: string
  what?: string
  when?: string
  who?: string
}

type FiltersVisibility = {
  deliveryMethod?: boolean
  what?: boolean
  when?: boolean
  who?: boolean
}

type WhatSelectRef = { selectItem: (value: string) => void }

const FILTERS_DEFAULT: FiltersVisibility = {
  deliveryMethod: true,
  what: true,
  when: true,
  who: true
}
const FILTERS_VALUE_DEFAULT: FiltersValue = {
  deliveryMethod: FILTER_DELIVERY_METHOD_ALL,
  what: FILTER_WHAT_ALL,
  when: FILTER_WHEN_UPCOMING,
  who: FILTER_WHO_ALL
}
const REFETCH_INTERVAL = 20 * 60 * 1000 // 20m

const emit = defineEmits<{
  'update:filtersValue': [value: FiltersValue | null]
}>()

const props = withDefaults(
  defineProps<{
    banner?: components['schemas']['ItemsBanners']
    hideFilters?: boolean
    hideInfoButton?: boolean
    filters?: FiltersVisibility
    filtersValue?: Partial<FiltersValue>
    fullScheduleHref?: string
    main?: boolean
    showEmbedCode?: boolean
    showPast?: boolean
    stickyTop?: number
    value: { title?: string; paragraph?: string }
    whatItems?: Array<{ value: string; [key: string]: unknown }>
    whatMenu?: components['schemas']['ItemsMenus']
    whatProducts?: Array<{ code: string; [key: string]: unknown }>
  }>(),
  {
    banner: undefined,
    hideFilters: false,
    hideInfoButton: false,
    filters: undefined,
    filtersValue: undefined,
    fullScheduleHref: '/training/schedule',
    main: false,
    showEmbedCode: false,
    showPast: false,
    stickyTop: MENU_BAR_HEIGHT,
    whatItems: undefined,
    whatMenu: undefined,
    whatProducts: undefined
  }
)

const { lgAndUp } = useDisplay()
const { items: deliveryMethods } = useDeliveryMethodsStoreOrThrow()
const {
  refetchData: refetchOccasions,
  showPast: showPastOccasions,
  yearMonthIndex
} = useOccasionsStoreOrThrow()
const { items: trainers } = useTrainerPersonsStoreOrThrow()
const isMounted = useMounted()
const container = useTemplateRef('container')
const variantList = useTemplateRef('variantList')
const occasionList = useTemplateRef('occasionList')
const filtersData = ref<FiltersValue | null>(null)
const filtersShown = ref(true)
const filtersAutoHideEnabled = ref(true)
const filtersOriginal = ref<FiltersValue | null>(null)
const shownFilters = computed(() => {
  return Object.assign({}, FILTERS_DEFAULT, props.filters)
})

const deliveryMethodItems = computed(() => {
  return [
    { title: 'All Formats', value: FILTER_DELIVERY_METHOD_ALL },
    ...(!deliveryMethods.value
      ? []
      : deliveryMethods.value.map(m => ({
          title: m.name ?? '',
          value: m.code ?? ''
        })))
  ]
})
const trainerItems = computed(() => {
  return [
    { title: 'All Trainers', value: FILTER_WHO_ALL },
    ...(!trainers.value
      ? []
      : trainers.value.map(p => ({
          title: p.name ?? '',
          value: p.code ?? ''
        })))
  ]
})
const whatSelect = ref<WhatSelectRef | null>(null)
const whenItems = computed(() => {
  return [
    {
      title: props.showPast ? 'Upcoming and Past' : 'Upcoming',
      value: FILTER_WHEN_UPCOMING
    },
    {
      title: 'Half Day',
      value: FILTER_WHEN_HALF_DAY
    },
    {
      title: 'Weekend',
      value: FILTER_WHEN_WEEKEND
    },
    ...Object.keys(yearMonthIndex.value).map(month => {
      return {
        title: DateTime.fromISO(month).toFormat('MMMM y'),
        value: month
      }
    })
  ]
})

let debounced: (() => void) | undefined

function onScroll(e: Event) {
  const target = e.target as Document
  if (
    lgAndUp.value &&
    filtersAutoHideEnabled.value &&
    filtersShown.value &&
    target.documentElement.scrollTop > window.innerHeight / 2
  )
    filtersShown.value = false
}

function resetFilters() {
  filtersData.value = Object.assign({}, filtersOriginal.value)

  if (whatSelect.value && filtersData.value && filtersData.value.what)
    whatSelect.value.selectItem(filtersData.value.what)
}

function selectWhatItem(newWhat: string) {
  if (filtersData.value) filtersData.value.what = newWhat
}

function toggleFilters() {
  if (typeof debounced === 'function') debounced()

  filtersAutoHideEnabled.value = false
  filtersShown.value = !filtersShown.value
}

useIntervalFn(async () => refetchOccasions(), REFETCH_INTERVAL)

onMounted(() => {
  watch(
    filtersData,
    newValue => {
      if (!debounced) {
        debounced = debounce(() => {
          filtersAutoHideEnabled.value = true
        }, 2000)
      } else if (container.value && lgAndUp.value) {
        debounced()
        filtersAutoHideEnabled.value = false

        scrollToRef(container, { behavior: 'instant' })
      }

      emit('update:filtersValue', newValue)
    },
    { deep: true }
  )

  watch(whatSelect, newValue => {
    if (newValue && filtersData.value && filtersData.value.what)
      newValue.selectItem(filtersData.value.what)
  })

  filtersData.value = Object.assign(
    {},
    FILTERS_VALUE_DEFAULT,
    props.filtersValue
  )
  filtersOriginal.value = Object.assign({}, filtersData.value)

  showPastOccasions.value = props.showPast ?? false
})
</script>
